import type { BrandId, ColorMode } from '@rammetec/design-system'
import { resolveFirebaseWebConfig } from './firebase.web'

type AppEnvironment = 'development' | 'staging' | 'production'

interface FirebaseEnv {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId?: string
}

interface Env {
  appEnvironment: AppEnvironment
  designSystemBrand: BrandId
  designSystemMode: ColorMode
  /** HTTP/WS base URL for the VectorHub server (e.g. http://localhost:3000). */
  serverUrl?: string
  /** Dev server port (default 6175). */
  consolePort: number
  /** WebSocket base URL for the optional monitor service. */
  monitorWsHost?: string
  /** WebSocket base URL for the System API (idle until Phase 8). */
  systemApiWsHost?: string
  /** HTTP base URL for the SysColect System API (where `/ai/v1` lives). */
  systemApiUrl?: string
  /**
   * When true in development, persist logs via the system-api transport
   * (spec SC 0058 / request 0061). Default false — local console stays quiet.
   */
  logPersistInDev: boolean
  /**
   * Shared SC Firebase project web config (D0001).
   * Required for auth — fill all VITE_FIREBASE_* vars in the root `.env`.
   */
  firebase: FirebaseEnv
}

function readString(name: string, fallback: string): string {
  const value = import.meta.env[name]
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback
}

function readOptional(name: string): string | undefined {
  const value = import.meta.env[name]
  if (typeof value !== 'string' || value.trim().length === 0) return undefined
  return value.trim()
}

/**
 * Config Firebase Web: env var vence, senao cai no mapa versionado
 * (`firebase.web.ts`) escolhido por `VITE_APP_ENV`. Ver spec 0009 F2.
 *
 * O fallback anterior era string vazia: sem as VITE_FIREBASE_* o console
 * inicializava o Firebase com config invalida e so quebrava no login. Agora o
 * mapa versionado cobre o caso e um vazio nos dois lados estoura no bootstrap.
 */
function readFirebaseEnv(): FirebaseEnv {
  const fallback = resolveFirebaseWebConfig(readString('VITE_APP_ENV', 'development'))
  const resolve = (name: string, value: string) => {
    const resolved = readOptional(name) ?? value
    if (resolved.length === 0) {
      throw new Error(`Missing Firebase config: ${name} (env var e firebase.web.ts vazios)`)
    }
    return resolved
  }

  return {
    apiKey: resolve('VITE_FIREBASE_API_KEY', fallback.apiKey),
    authDomain: resolve('VITE_FIREBASE_AUTH_DOMAIN', fallback.authDomain),
    projectId: resolve('VITE_FIREBASE_PROJECT_ID', fallback.projectId),
    storageBucket: resolve('VITE_FIREBASE_STORAGE_BUCKET', fallback.storageBucket),
    messagingSenderId: resolve('VITE_FIREBASE_MESSAGING_SENDER_ID', fallback.messagingSenderId),
    appId: resolve('VITE_FIREBASE_APP_ID', fallback.appId),
    measurementId: readOptional('VITE_FIREBASE_MEASUREMENT_ID') ?? fallback.measurementId,
  }
}

function readBoolean(name: string, fallback: boolean): boolean {
  const value = import.meta.env[name]
  if (typeof value !== 'string') return fallback
  return value.trim() === 'true'
}

function readAppEnvironment(): AppEnvironment {
  const value = readString('VITE_APP_ENV', 'development')
  if (value === 'development' || value === 'staging' || value === 'production') return value
  throw new Error(`Invalid VITE_APP_ENV: ${value}`)
}

function readColorMode(): ColorMode {
  const value = readString('VITE_DS_MODE', 'light')
  if (value === 'light' || value === 'dark' || value === 'auto') return value
  throw new Error(`Invalid VITE_DS_MODE: ${value}`)
}

export const env: Env = {
  appEnvironment: readAppEnvironment(),
  designSystemBrand: readString('VITE_DS_BRAND', 'vectorhub') as BrandId,
  designSystemMode: readColorMode(),
  serverUrl: readOptional('VITE_SERVER_URL'),
  consolePort: Number.parseInt(readString('VECTORHUB_CONSOLE_PORT', '5200'), 10),
  monitorWsHost: readOptional('VITE_MONITOR_WS_HOST'),
  systemApiWsHost: readOptional('VITE_SYSTEM_API_WS_HOST'),
  systemApiUrl: readOptional('VITE_SYSTEM_API_URL'),
  logPersistInDev: readBoolean('VITE_LOG_PERSIST_IN_DEV', false),
  firebase: readFirebaseEnv(),
}
